import { config } from '@/config';
import axios from 'axios';
import { ROUTES } from '@/types/routes';
import { generateCsrfToken } from '@/api/security';
import { decodeTokenSub, setAccessToken } from './tokenStorage';
import { syncSocketAuth, disconnectSocket } from '@/client/socket';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// TCX-824: `decodeTokenSub` moved to tokenStorage.ts (which must not import
// from this module — cycle). Re-exported here so existing importers (incl.
// axiosInstance.spec.ts) keep working unchanged.
export { decodeTokenSub };

const BASE_URL = config.VITE_BE_ENDPOINT;

/**
 * TCX-795: build the refresh-endpoint URL without depending on whether
 * VITE_BE_ENDPOINT carries a trailing slash. The repo convention IS a
 * trailing slash (api/auth.ts concatenates `${config.VITE_BE_ENDPOINT}auth/login`
 * with no separator), so the previous `${BASE_URL}/auth/refresh` produced
 * `/api//auth/refresh` — which the backend rejects with 401 (verified against
 * a running stack: the same freshly-issued refresh token gets 201 on
 * `/api/auth/refresh` and 401 on `/api//auth/refresh`). Every refresh attempt
 * therefore failed, was classified as a definitively-invalid session, and
 * evicted the user — the exact eviction class this ticket exists to stop.
 * Pure and exported so the join is unit-tested for both env shapes.
 */
export const buildRefreshUrl = (base: string): string =>
  `${base.replace(/\/+$/, '')}/auth/refresh`;

/**
 * TCX-857: regenerate a few seconds BEFORE the token's real expiry so a request
 * doesn't race across the boundary and a slightly-lagging client clock still
 * refreshes in time.
 */
const CSRF_EXPIRY_SKEW_SECONDS = 30;

/**
 * TCX-857: decode the backend CSRF token's payload.
 *
 * The token is `base64url(JSON.stringify({sub,nonce,iat,exp})).hmac`
 * (packages/nestJs/src/security/csrf.service.ts), so — unlike a JWT, whose
 * payload is the SECOND segment (see `decodeTokenSub`) — the payload here is the
 * FIRST segment. Returns null for anything undecodable so callers can decide to
 * regenerate rather than reason about a token they can't read. We only read an
 * already-issued token's claims; the HMAC is the backend's to verify.
 */
const decodeCsrfPayload = (
  token: string,
): { sub?: unknown; exp?: unknown } | null => {
  try {
    const payloadSegment = token.split('.')[0];
    if (!payloadSegment) return null;
    // Normalise base64url -> base64 before decoding (mirrors decodeTokenSub).
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as { sub?: unknown; exp?: unknown };
  } catch {
    return null;
  }
};

/**
 * TCX-857: decode the CSRF token's subject (`sub`), or null when it is
 * missing/invalid/undecodable. The backend mints the token as
 * `generateToken(userId)`, so `sub` identifies the account it belongs to.
 */
const decodeCsrfSub = (token: string): string | null => {
  const payload = decodeCsrfPayload(token);
  return payload && typeof payload.sub === 'string' && payload.sub.length > 0
    ? payload.sub
    : null;
};

/**
 * TCX-857: Should the cached CSRF token be regenerated before use?
 *
 * The backend CSRF token has a 1h TTL. The request interceptor previously
 * generated it only when ABSENT, so a session outliving the TTL (its access
 * token keeps refreshing, this token does not) kept resending a
 * present-but-expired token and every mutating request 401'd.
 *
 * Treat the token as expired a skew-window early. Anything undecodable /
 * missing `exp` is treated as expired so the interceptor regenerates rather
 * than send a token it can't reason about.
 *
 * Pure predicate (reads only the argument + the clock) so it unit-tests in
 * isolation alongside the TCX-795 predicates.
 */
export const isCsrfTokenExpired = (token: string): boolean => {
  const payload = decodeCsrfPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + CSRF_EXPIRY_SKEW_SECONDS;
};

/**
 * TCX-857: is the cached CSRF token missing a usable subject?
 *
 * The backend rejects any mutating request whose CSRF `sub` doesn't equal the
 * caller (csrf.middleware.ts checks `payload.sub === userId`), so a token with
 * no decodable `sub` can NEVER be accepted — even if its `exp` is still in the
 * future. Regenerate it rather than send a token the backend is certain to
 * reject. Pure predicate, unit-tested in isolation.
 */
export const isCsrfTokenSubjectMissing = (token: string): boolean =>
  decodeCsrfSub(token) === null;

/**
 * TCX-857 (security): does the cached CSRF token belong to a DIFFERENT account
 * than the one that now owns the session?
 *
 * The backend binds each CSRF token to a `sub` and mints it as
 * `generateToken(req.user.userId)` (security/csrf.service.ts + security.controller.ts),
 * so the CSRF `sub` always equals the authenticated user's id — i.e. the access
 * token's `sub`. The CSRF middleware rejects (401) any mutating request whose
 * CSRF `sub` doesn't match the caller. After a cross-tab account switch, TCX-824
 * reloads the stale tab — but sessionStorage SURVIVES a reload, so it can still
 * hold user A's UNEXPIRED CSRF token while localStorage now holds user B's access
 * token. `isCsrfTokenExpired` won't catch that (A's token is still in date), so
 * the interceptor would resend it and every B mutation would 401.
 *
 * Returns true ONLY when both subjects decode and genuinely differ. A missing /
 * undecodable subject on either side is NOT a definitive mismatch (return
 * false): an absent access token means there is nothing to contradict (e.g. a
 * pre-login mutating request), and an undecodable CSRF token is already handled
 * by `isCsrfTokenExpired`. This keeps regeneration limited to the real
 * account-switch case rather than churning on every edge.
 *
 * CSRF `sub` is the FIRST segment; the access token is a JWT whose `sub` is the
 * SECOND segment (decoded by `decodeTokenSub`). Pure predicate — no storage
 * access — so it unit-tests in isolation.
 */
export const isCsrfTokenForDifferentSubject = (
  csrfToken: string,
  accessToken: string | null | undefined,
): boolean => {
  const csrfSub = decodeCsrfSub(csrfToken);
  const accessSub = decodeTokenSub(accessToken);
  return csrfSub !== null && accessSub !== null && csrfSub !== accessSub;
};

/**
 * TCX-795: Decide whether a failed token refresh means the session is
 * DEFINITIVELY dead (force logout) or merely TRANSIENTLY failing (keep the
 * session, surface the error, allow retry).
 *
 * The `/auth/refresh` endpoint returns 401 (and could return 403) only when
 * the refresh token itself is truly bad — invalid / revoked / expired
 * (see auth.service.refreshTokens). Everything else — no HTTP response at all
 * (network drop, timeout, CORS), a 5xx server error, or a 429 throttle/
 * contention response (the refresh route is per-IP rate-limited, the exact
 * shared-office class that produced TCX-599/600) — is transient and must NOT
 * evict the user or wipe their unsaved work.
 *
 * Pure predicate: input is the error thrown by the refresh call, output is a
 * boolean. Kept side-effect free so it can be unit-tested in isolation.
 */
export const isDefinitivelyInvalidSession = (
  refreshError: unknown,
): boolean => {
  const status = axios.isAxiosError(refreshError)
    ? refreshError.response?.status
    : undefined;

  // No response object => network error / timeout / CORS => transient.
  if (typeof status !== 'number') {
    return false;
  }

  // Only a hard auth rejection from the refresh endpoint kills the session.
  // 5xx (server error) and 429 (throttle/contention) are transient and fall
  // through to `false`.
  return status === 401 || status === 403;
};

/**
 * TCX-795: Detect a benign cross-tab refresh-token rotation.
 *
 * Multi-tab contention: two tabs with an expired access token both POST the
 * same refresh token to `/auth/refresh`. The backend rotates the token —
 * revoking the submitted one before issuing a replacement
 * (auth.service.refreshTokens) — and the winning tab writes the new tokens to
 * shared localStorage. The losing tab's in-flight refresh (still carrying the
 * now-revoked token) then 401s. That 401 must NOT evict: the session is alive,
 * another tab just rotated it.
 *
 * We recognise this by comparing the refresh token THIS request submitted
 * (captured at send time) against the value currently in localStorage. If it
 * changed to a different non-empty value, another tab rotated it and we should
 * recover rather than evict. If it is unchanged (or was cleared), the 401 is a
 * genuinely-dead session and eviction proceeds.
 *
 * Pure predicate — no localStorage access here so it can be unit-tested in
 * isolation, mirroring `isDefinitivelyInvalidSession`.
 */
export const didRefreshTokenRotate = (
  submittedRefreshToken: string | null,
  currentRefreshToken: string | null,
): boolean =>
  currentRefreshToken !== null && currentRefreshToken !== submittedRefreshToken;

/**
 * TCX-795 (security): the tokens in shared localStorage changing during our
 * in-flight refresh is only a BENIGN same-user rotation when the new tokens
 * still belong to the SAME account. If another tab logged out and signed in as
 * a DIFFERENT user mid-refresh, the stored tokens now belong to someone else —
 * replaying this tab's original (old-session) request under the new user's
 * access token would execute the old session's mutation as the new account
 * (cross-account request replay).
 *
 * Prove same-subject before recovering: decode `sub` from the access token the
 * original request was actually sent with, and from the access token now in
 * storage that we would recover with. Recover ONLY when both decode to the same
 * non-empty `sub`. Any mismatch — or an undecodable token on either side —
 * means it is a genuinely different session and we must not replay.
 *
 * Pure predicate — no localStorage access here, so it unit-tests in isolation
 * alongside the other TCX-795 predicates.
 */
export const isSameSubjectRecovery = (
  originalRequestAccessToken: string | null | undefined,
  recoveredAccessToken: string | null | undefined,
): boolean => {
  const originalSub = decodeTokenSub(originalRequestAccessToken);
  const recoveredSub = decodeTokenSub(recoveredAccessToken);
  return originalSub !== null && originalSub === recoveredSub;
};

/**
 * TCX-824 (security): decide whether a freshly-issued access token (a rotated
 * `Authorization` response header, or an explicit /auth/refresh success body)
 * is safe to write to shared localStorage.
 *
 * The vector: user A's request is in flight while another tab signs in as user
 * B. The backend's RefreshTokenMiddleware.applyTokens can rotate A's expired
 * token and return it as an `Authorization` header on A's response. If we write
 * it unconditionally, A's token clobbers B's token in shared localStorage — and
 * the cross-tab storage listener then reloads B's tab INTO A. So a rotated token
 * is only safe to persist when NO other account has taken over shared storage
 * since: the incoming token's subject must equal the subject currently in
 * storage (or storage must be empty — a first login writing its own token).
 *
 * Returns true ONLY when the incoming token belongs to the SAME account that
 * currently owns storage. It returns false when:
 *   - storage is EMPTY. A rotation/refresh guard has no legitimate first-write
 *     case — real first logins write through `setAccessToken` directly
 *     (api/auth.ts, components/auth/*), NOT through this path. Empty storage
 *     here means another tab LOGGED OUT (cleared the token) mid-flight;
 *     persisting the in-flight rotated token would RESURRECT the dead session.
 *   - storage belongs to a DIFFERENT account (another tab signed in), or
 *   - the incoming token is undecodable.
 * In every false case the caller must drop the incoming token.
 *
 * Pure predicate (no localStorage access) so it unit-tests in isolation.
 */
export const canPersistRefreshedToken = (
  incomingAccessToken: string | null | undefined,
  currentStoredAccessToken: string | null | undefined,
): boolean => {
  const incomingSub = decodeTokenSub(incomingAccessToken);
  if (incomingSub === null) return false; // undecodable/garbage → never write
  const currentSub = decodeTokenSub(currentStoredAccessToken);
  // Persist ONLY when the same account still owns storage. Empty storage
  // (logout-clear race) and a different subject both mean: do not write.
  return currentSub !== null && currentSub === incomingSub;
};

// TCX-838: session generation. Bumped whenever the session is torn down
// (logout / eviction). The request interceptor stamps the current generation on
// each request; the response interceptor refuses to adopt rotated tokens or
// retry for a request whose generation is stale — otherwise a refresh response
// that lands AFTER logout would re-write the just-cleared tokens back into
// localStorage and resurrect the session.
let sessionGeneration = 0;

export const endSession = (): void => {
  sessionGeneration += 1;
  // Drop the in-memory Authorization default so no teardown path (logout,
  // evictSession, or changePassword's eviction) can leave a stale bearer for a
  // later request to inherit — the request interceptor only sets Authorization
  // when localStorage still has a token. `api` is created below and always
  // initialized by the time this runs.
  delete api.defaults.headers.common['Authorization'];
};

// A request is stale if it was issued under an earlier session generation than
// the current one (a logout/eviction happened while it was in flight). An
// unstamped request (undefined) is treated as current — never block on a
// missing stamp.
export const isSessionStale = (
  requestConfig: unknown,
  currentGeneration: number,
): boolean => {
  const gen = (requestConfig as { __sessionGen?: number } | undefined)
    ?.__sessionGen;
  return gen !== undefined && gen !== currentGeneration;
};

/**
 * TCX-795: Single source of truth for session eviction. Any code path that
 * needs to force a logout (the response interceptor below, and callers such as
 * api/user.ts) must go through this function so the "clear + redirect"
 * behavior lives in exactly one place.
 */
export const evictSession = (): void => {
  endSession();
  // TCX-838: dropping the session must also drop its authenticated socket, or a
  // tab that already joined chat/user rooms keeps a live socket as the evicted
  // user until navigation. endSession() only bumps the generation + clears the
  // bearer; the socket is a separate transport and must be torn down explicitly.
  disconnectSocket();
  localStorage.clear();
  sessionStorage.clear();
  window.location.replace(ROUTES.SIGN_IN);
};

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'ngrok-skip-browser-warning': true,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // TCX-838: capture the session generation at interceptor ENTRY, before the
    // awaited CSRF fetch below. Stamping after the await could tag a request
    // that started pre-logout with the post-logout generation and wrongly treat
    // its response as current.
    const requestGeneration = sessionGeneration;

    if (['post', 'put', 'patch', 'delete'].includes(config.method ?? '')) {
      let csrfToken = sessionStorage.getItem('csrfToken');
      // TCX-857: read the access token here only to DECIDE whether the cached
      // CSRF token still belongs to the current account. This is a
      // regeneration decision, not the value attached to the request — the
      // Authorization header is still set from a fresh read AFTER the awaited
      // CSRF fetch below (TCX-838), so this early read can't reintroduce the
      // stale-token attach that TCX-838 closed.
      const accessTokenForCsrf = localStorage.getItem('accessToken');

      // Lazily generate and cache the CSRF token when needed. Regenerate when:
      //  - absent;
      //  - EXPIRED (TCX-857) — it has a 1h TTL and the session outlives it (the
      //    access token is refreshed, this token was not), so a
      //    present-but-expired token would be resent and the backend would 401
      //    every mutating request;
      //  - its SUBJECT is missing/invalid (TCX-857) — the backend checks
      //    `payload.sub === userId`, so a token with no usable `sub` can never
      //    be accepted regardless of its exp;
      //  - bound to a DIFFERENT account (TCX-857) — a cross-tab account switch
      //    (TCX-824) reloads the stale tab, but sessionStorage survives the
      //    reload and can still hold the previous user's UNEXPIRED token while
      //    localStorage now holds the new user's access token; resending it
      //    401s on the subject mismatch.
      if (
        !csrfToken ||
        isCsrfTokenExpired(csrfToken) ||
        isCsrfTokenSubjectMissing(csrfToken) ||
        isCsrfTokenForDifferentSubject(csrfToken, accessTokenForCsrf)
      ) {
        csrfToken = await generateCsrfToken();
      }

      // TCX-857 (security): the account can change — another tab logs in as a
      // different user — WHILE generateCsrfToken() is awaited above. Re-read the
      // authoritative access token AFTER the await and confirm the CSRF token we
      // are about to attach still belongs to it. If the subjects diverge, the
      // session switched mid-flight: this request was composed under the old
      // account, so sending it with the new account's bearer would replay the
      // old mutation as the new user. ABORT rather than replay — TCX-824's
      // storage listener reloads the tab into the account that now owns the
      // session. (The pre-await read above cannot see a switch that lands during
      // the await; only this post-await revalidation can.)
      const accessTokenAfterCsrf = localStorage.getItem('accessToken');
      if (isCsrfTokenForDifferentSubject(csrfToken, accessTokenAfterCsrf)) {
        throw new Error(
          'Account switched during CSRF token generation; aborting stale request',
        );
      }

      config.headers = config.headers || {};
      config.headers['x-csrf-token'] = csrfToken;
    }

    // TCX-838: read the tokens AFTER the awaited CSRF fetch, not before — a
    // logout/teardown that lands while CSRF generation is pending would
    // otherwise leave a stale closed-over token attached to this request (whose
    // mutation would then reach the server under the dead session).
    const token = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    config.headers = config.headers || {};
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // No stored token — strip any Authorization so a stale in-memory default
      // (set by an earlier refresh) can't be sent after a session teardown,
      // regardless of which teardown path ran. This is the single point that
      // makes "no token => no bearer" hold for every request.
      delete config.headers['Authorization'];
    }
    if (refreshToken) {
      config.headers['x-refresh-token'] = refreshToken;
    } else {
      delete config.headers['x-refresh-token'];
    }

    // TCX-838: stamp the generation captured at entry so the response handler
    // can tell whether this request predates a logout/eviction.
    (
      config as InternalAxiosRequestConfig & { __sessionGen?: number }
    ).__sessionGen = requestGeneration;

    return config;
  },
  (error) => Promise.reject(error),
);

// TCX-838: cross-tab logout. When another tab clears storage or removes the
// auth tokens (logout / eviction), end the session in THIS tab too, so an
// in-flight response here can't write the just-cleared tokens back and
// resurrect it. Only token REMOVAL triggers this — a cross-tab token ROTATION
// (TCX-795) sets a NEW value, which we intentionally ignore so legitimate
// same-user rotations are still adopted. A cross-tab account SWITCH (a new
// non-null token for a DIFFERENT subject) is handled separately by the
// tokenStorage.ts listener (TCX-824), which reloads the stale tab.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    const storageCleared = event.key === null;
    const tokenRemoved =
      (event.key === 'accessToken' || event.key === 'refreshToken') &&
      event.newValue === null;
    if (storageCleared || tokenRemoved) {
      // Always bump the session generation so an in-flight response from the old
      // session can't resurrect it (TCX-838 core). This touches no storage, so
      // it's safe regardless of what another tab did next.
      endSession();
      // Guard the async race — storage events are async and localStorage is
      // SHARED, so by the time this fires another tab may have logged in again.
      // Only treat this as a real logout when the tokens are STILL absent.
      if (!localStorage.getItem('accessToken')) {
        // Genuine cross-tab logout → full client teardown. The socket is a
        // separate transport that endSession doesn't touch, so a tab that
        // already joined chat/user rooms would keep a live authenticated socket
        // as the logged-out user. Drop it here too so cross-tab logout kills the
        // socket, exactly as a same-tab logout does; a later same-tab login
        // calls reconnectSocket(), which clears the intentional-disconnect flag.
        // Never localStorage.clear() here — that would wipe a concurrent login
        // and log the other tab back out (codex review).
        disconnectSocket();
        window.location.replace(ROUTES.SIGN_IN);
      } else {
        // Relogin race: another tab cleared storage then signed in again before
        // this async handler ran. Adopt the new session and re-authenticate the
        // socket for the new identity (syncSocketAuth reconnects because the
        // stored token changed) rather than tearing it down — disconnecting here
        // would leave this tab logged in with a permanently dead socket, since
        // the login happened in another tab and no same-tab reconnect runs
        // (codex review).
        syncSocketAuth();
      }
    }
  });
}

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const accessToken = response.headers['authorization'];
    const refreshToken = response.headers['x-refresh-token'];

    // TCX-838: don't adopt a rotated token if the request predates a
    // logout/eviction — otherwise a response landing after logout re-writes the
    // just-cleared tokens back into localStorage and resurrects the session.
    if (!isSessionStale(response.config, sessionGeneration)) {
      if (accessToken) {
        // TCX-824 (security): the backend can rotate an in-flight request's
        // token and return it here. If another tab signed in as a DIFFERENT
        // account while this request was in flight, this rotated token belongs
        // to the OLD account — writing it would clobber the new account's token
        // in shared localStorage and reload the new tab into the old account.
        // Only persist when the rotated token's subject still matches the
        // account that owns storage. Route through setAccessToken so the tab's
        // tracked subject stays current (storage events don't fire in the
        // writing tab).
        const rotatedAccessToken = accessToken.split(' ')[1];
        if (
          canPersistRefreshedToken(
            rotatedAccessToken,
            localStorage.getItem('accessToken'),
          )
        ) {
          setAccessToken(rotatedAccessToken);
          // The paired refresh token is only valid alongside its access token —
          // write it only when we accepted the access token, never on its own.
          if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
          }
          // TCX-820: the sliding-window refresh just rotated the access token on
          // this response — re-authenticate the chat socket so it re-handshakes
          // with the new token. Gated on acceptance: a rejected cross-account
          // token (TCX-824) must NOT drive a socket re-auth into the wrong
          // identity.
          syncSocketAuth();
        }
      } else if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // TCX-838: a request issued before a logout/eviction must not drive a
      // refresh (it would rotate the NEW session's token, revoking it) or queue
      // for retry — reject it up front, before touching the refresh machinery.
      if (isSessionStale(originalRequest, sessionGeneration)) {
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // TCX-795: Capture the exact refresh token this tab is about to submit,
      // read from shared localStorage AT SEND TIME. On failure we compare it
      // against the then-current stored value to distinguish a genuinely-dead
      // session from a benign cross-tab token rotation.
      const submittedRefreshToken = localStorage.getItem('refreshToken');
      if (submittedRefreshToken) {
        try {
          const response = await axios.post(
            buildRefreshUrl(BASE_URL),
            { refreshToken: submittedRefreshToken },
            {
              headers: { 'ngrok-skip-browser-warning': true },
            },
          );

          const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
            response.data;

          // TCX-838: if the session was torn down (logout/eviction) while this
          // refresh was in flight, drop the rotated tokens and fail — do not
          // resurrect the session or retry the request. Checked BEFORE the
          // account-switch guard: a teardown redirects via its own path and must
          // not trigger a spurious reload here.
          if (isSessionStale(originalRequest, sessionGeneration)) {
            processQueue(error, null);
            return Promise.reject(error);
          }

          // TCX-824 (security): while this refresh was in flight, another tab
          // may have signed in as a DIFFERENT account, so shared localStorage
          // now belongs to someone else. Writing THIS tab's refreshed token
          // would clobber the new account's token and (via the storage
          // listener) reload the new tab into the old account. If storage no
          // longer belongs to our account, DO NOT persist; this tab is stale —
          // reject the queued requests and reload so it re-initializes as the
          // account that now owns the session (matching the different-account
          // branch of the catch below). Never replay this tab's old-session
          // request under the new account's credentials.
          if (
            !canPersistRefreshedToken(
              newAccessToken,
              localStorage.getItem('accessToken'),
            )
          ) {
            const staleSessionError = new Error(
              'Account switched in another tab during token refresh',
            );
            processQueue(staleSessionError, null);
            window.location.reload();
            return Promise.reject(staleSessionError);
          }

          // TCX-824: access-token writes go through setAccessToken so this
          // tab's tracked subject stays current (see client/tokenStorage.ts).
          setAccessToken(newAccessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          // TCX-820: token refreshed on the 401 path — re-handshake the chat
          // socket with the new token.
          syncSocketAuth();

          api.defaults.headers.common['Authorization'] =
            `Bearer ${newAccessToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);
          return api(originalRequest);
        } catch (refreshError) {
          // TCX-795: Multi-tab refresh contention. When two tabs hit an expired
          // access token together, both POST the same refresh token. The first
          // succeeds and stores rotated tokens in shared localStorage; the
          // backend revokes the submitted token before storing the replacement
          // (auth.service.refreshTokens). The second tab's in-flight refresh —
          // carrying the now-revoked token — then 401s. That 401 is NOT a dead
          // session: the winning tab has already put a fresh refresh token in
          // localStorage. Detect that by re-reading the stored token: if it
          // CHANGED since we captured `submittedRefreshToken`, another tab
          // rotated it, so recover (retry with the freshly-stored access token)
          // instead of evicting.
          const currentRefreshToken = localStorage.getItem('refreshToken');
          const rotatedByAnotherTab = didRefreshTokenRotate(
            submittedRefreshToken,
            currentRefreshToken,
          );

          if (
            rotatedByAnotherTab &&
            isDefinitivelyInvalidSession(refreshError)
          ) {
            // Another tab changed the stored tokens mid-refresh. Before we
            // replay this request with them, PROVE it is the same account.
            //
            // TCX-795 (security): a changed token is only a benign same-user
            // rotation if the new access token still belongs to the same
            // subject. If another tab logged out and signed in as a DIFFERENT
            // user while we were refreshing, retrying this tab's stale
            // originalRequest (e.g. a pending PATCH/POST from the OLD session)
            // with the NEW user's access token would replay the old session's
            // mutation under the newly signed-in account. Compare the `sub` of
            // the access token this request was SENT with (from its own
            // Authorization header) against the `sub` of the access token we
            // would recover with.
            const recoveredAccessToken = localStorage.getItem('accessToken');
            const originalAuthHeader = String(
              originalRequest.headers?.['Authorization'] ?? '',
            );
            const originalRequestAccessToken = originalAuthHeader.startsWith(
              'Bearer ',
            )
              ? originalAuthHeader.slice('Bearer '.length)
              : null;

            if (
              isSameSubjectRecovery(
                originalRequestAccessToken,
                recoveredAccessToken,
              ) &&
              // TCX-838: don't recover/retry if this tab logged out mid-refresh.
              !isSessionStale(originalRequest, sessionGeneration)
            ) {
              // Same account rotated the token. Use the access token it stored
              // and retry this request plus everything queued behind it.
              api.defaults.headers.common['Authorization'] =
                `Bearer ${recoveredAccessToken}`;
              originalRequest.headers['Authorization'] =
                `Bearer ${recoveredAccessToken}`;
              // TCX-820: another tab rotated our token — re-handshake this tab's
              // chat socket with the recovered token so it doesn't linger on the
              // stale one.
              syncSocketAuth();
              processQueue(null, recoveredAccessToken);
              return api(originalRequest);
            }

            // Tokens changed but belong to a DIFFERENT account (or a token
            // could not be decoded). Do NOT replay this request under the new
            // user. This tab's session is genuinely gone, so reject; the new
            // account's own tokens remain in storage untouched (no eviction —
            // wiping them would sign the active account out).
            //
            // TCX-824: but this tab is still RENDERING the old account's
            // state, and the request interceptor reads the latest stored
            // token — a later click here would go out under the new account.
            // Reload WITHOUT clearing storage: reloading (not evicting) hands
            // the tab to the account that now owns the session cleanly.
            processQueue(refreshError, null);
            window.location.reload();
            return Promise.reject(refreshError);
          }

          processQueue(refreshError, null);
          // Only evict on a definitively-invalid session that was NOT a
          // cross-tab rotation. Transient failures (network/timeout, 5xx, 429
          // throttle) reject the error so callers can surface/retry it without
          // wiping unsaved work.
          if (isDefinitivelyInvalidSession(refreshError)) {
            evictSession();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token to attempt a refresh with: the session cannot be
        // renewed, so this is a definitively-invalid session.
        evictSession();
      }
    }

    return Promise.reject(error);
  },
);

export default api;
